import { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SEOHead, trackEvent } from '@/components/SEOHead';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Calendar, Tag, ArrowLeft, Linkedin, Twitter, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function InsightArticlePage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/posts/${slug}`)
      .then(r => { setPost(r.data); setLoading(false); trackEvent('blog_post_read', { post_slug: slug }); })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#4E6974]">Loading...</div>;
  if (!post) return <div className="min-h-screen flex items-center justify-center text-[#4E6974]">Article not found</div>;

  const shareUrl = encodeURIComponent(`https://nuvantatradepath.com.au/insights/${slug}`);
  const shareTitle = encodeURIComponent(post.title);

  return (
    <div className="min-h-screen bg-white" data-testid="insight-article-page">
      <SEOHead
        title={`${post.title} | Nuvanta TradePath Insights`}
        description={post.meta_description || post.excerpt}
        ogImage={post.og_image}
        ogType="article"
      />
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A2533]/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-white font-bold text-xl">Nuvanta Solutions</span>
            <span className="text-[#4DD0E1]">|</span>
            <span className="text-[#4DD0E1] font-semibold">TradePath</span>
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/insights" className="text-white/70 hover:text-white text-sm">Insights</Link>
            <Link to="/signin" className="text-white/70 hover:text-white text-sm border border-white/20 px-4 py-1.5 rounded-lg">Sign in</Link>
          </div>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 pt-28 pb-16">
        <Link to="/insights" className="flex items-center gap-1.5 text-[#0097A7] text-sm mb-6 hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to Insights
        </Link>

        <div className="flex items-center gap-3 text-xs text-[#B0BEC5] mb-4">
          <span className="flex items-center gap-1"><Tag className="h-3 w-3" /> {post.category}</span>
          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(post.published_at)}</span>
        </div>

        <h1 className="text-3xl font-bold text-[#0A2533] mb-4" style={{ fontFamily: "'Trebuchet MS', sans-serif" }}>{post.title}</h1>

        {post.excerpt && <p className="text-lg text-[#4E6974] mb-8 leading-relaxed">{post.excerpt}</p>}

        <div className="prose prose-slate max-w-none mb-10" style={{ fontFamily: "'Calibri', sans-serif" }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content || '') }} />

        {/* Author */}
        <div className="border-t border-[#E8ECF0] pt-6 mb-8">
          <p className="text-xs text-[#B0BEC5] uppercase tracking-wide mb-1">Written by</p>
          <p className="text-sm font-medium text-[#0A2533]">{post.author || 'Nuvanta TradePath Team'}</p>
        </div>

        {/* Share */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-[#4E6974]">Share:</span>
          <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`} target="_blank" rel="noopener noreferrer"
            className="p-2 rounded-lg border border-[#E0E0E0] hover:border-[#0097A7] text-[#4E6974] hover:text-[#0097A7] transition-colors">
            <Linkedin className="h-4 w-4" />
          </a>
          <a href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`} target="_blank" rel="noopener noreferrer"
            className="p-2 rounded-lg border border-[#E0E0E0] hover:border-[#0097A7] text-[#4E6974] hover:text-[#0097A7] transition-colors">
            <Twitter className="h-4 w-4" />
          </a>
          <a href={`mailto:?subject=${shareTitle}&body=Read this article: https://nuvantatradepath.com.au/insights/${slug}`}
            className="p-2 rounded-lg border border-[#E0E0E0] hover:border-[#0097A7] text-[#4E6974] hover:text-[#0097A7] transition-colors">
            <Mail className="h-4 w-4" />
          </a>
        </div>

        {/* CTA */}
        <div className="bg-[#F8FAFB] border border-[#E8ECF0] rounded-xl p-6 mt-10 text-center">
          <h3 className="text-lg font-bold text-[#0A2533] mb-2" style={{ fontFamily: "'Trebuchet MS', sans-serif" }}>
            Want to see these risks in real time?
          </h3>
          <p className="text-sm text-[#4E6974] mb-4">
            TradePath monitors 42 countries across 12 data sources so you don't have to.
          </p>
          <Link to="/register" className="inline-block px-6 py-2.5 bg-[#00707C] hover:bg-[#005F6A] text-white text-sm font-medium rounded-lg transition-colors">
            Start your free trial
          </Link>
        </div>
      </article>

      <footer className="bg-[#0A2533] py-8">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <p className="text-white/40 text-xs">Nuvanta Solutions Pty Ltd (ABN 61 689 692 484)</p>
          <div className="flex gap-6">
            <Link to="/insights" className="text-white/60 hover:text-white text-xs">Insights</Link>
            <Link to="/terms" className="text-white/60 hover:text-white text-xs">Terms of Service</Link>
            <Link to="/privacy" className="text-white/60 hover:text-white text-xs">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
